import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { WorkflowListProps } from '../types';
import { api } from '../api';

export const WorkflowList: React.FC<WorkflowListProps> = ({
  workflows,
  onCreateWorkflow,
  onDeleteWorkflow,
  getWorkflowAgents,
}) => {
  const [newWorkflowName, setNewWorkflowName] = React.useState('');
  const [isCreating, setIsCreating] = React.useState(false);
  const [workflowToDelete, setWorkflowToDelete] = React.useState<{ id: string; name: string; hasAgents: boolean } | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = React.useState('');
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [workflowAgentCounts, setWorkflowAgentCounts] = React.useState<Record<string, number>>({});
  const navigate = useNavigate();

  // Load agent counts for all workflows
  React.useEffect(() => {
    const loadAgentCounts = async () => {
      const counts: Record<string, number> = {};
      for (const workflow of workflows) {
        try {
          const agents = await getWorkflowAgents(workflow.id);
          counts[workflow.id] = agents.length;
        } catch (err) {
          console.error(`Failed to get agents for workflow ${workflow.id}:`, err);
          counts[workflow.id] = 0;
        }
      }
      setWorkflowAgentCounts(counts);
    };

    loadAgentCounts();
  }, [workflows, getWorkflowAgents]);

  const handleCreateWorkflow = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newWorkflowName.trim()) return;
    
    try {
      const workflow = await onCreateWorkflow(newWorkflowName);
      setNewWorkflowName('');
      setIsCreating(false);
      navigate(`/workflows/${workflow.id}`);
    } catch (err) {
      console.error('Failed to create workflow:', err);
    }
  };

  const handleDeleteClick = async (workflowId: string, workflowName: string) => {
    try {
      const agents = await getWorkflowAgents(workflowId);
      setWorkflowToDelete({
        id: workflowId,
        name: workflowName,
        hasAgents: agents.length > 0
      });
    } catch (err) {
      console.error('Failed to check workflow agents:', err);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!workflowToDelete) return;

    // For workflows with agents, require "DELETE" confirmation
    if (workflowToDelete.hasAgents && deleteConfirmation !== 'DELETE') {
      return;
    }

    setIsDeleting(true);
    try {
      // Get all agents for this workflow
      const agents = await getWorkflowAgents(workflowToDelete.id);
      
      // Delete all associated agents first
      for (const agent of agents) {
        try {
          await api.deleteAgent(agent.id);
        } catch (err) {
          console.error(`Failed to delete agent ${agent.id}:`, err);
        }
      }

      // Then delete the workflow
      await onDeleteWorkflow(workflowToDelete.id);
      setWorkflowToDelete(null);
      setDeleteConfirmation('');
    } catch (err) {
      console.error('Failed to delete workflow:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Show loading state if workflows is null or undefined
  if (!workflows) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Workflows</h2>
          <button
            onClick={() => setIsCreating(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Create Workflow
          </button>
        </div>
        <div className="bg-white shadow rounded-lg p-6 text-center">
          <p className="text-gray-500 mb-4">Loading workflows...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Workflows</h2>
        <button
          onClick={() => setIsCreating(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Create Workflow
        </button>
      </div>

      {/* Delete Confirmation Modal */}
      {workflowToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Delete Workflow
            </h3>
            <p className="text-gray-500 mb-4">
              Are you sure you want to delete the workflow "{workflowToDelete.name}"?
              {workflowToDelete.hasAgents && (
                <span className="block mt-2 text-red-600">
                  This workflow has associated agents that will also be deleted.
                  Type "DELETE" to confirm.
                </span>
              )}
            </p>
            {workflowToDelete.hasAgents && (
              <input
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="Type DELETE to confirm"
                className="w-full p-2 border rounded mb-4"
              />
            )}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setWorkflowToDelete(null);
                  setDeleteConfirmation('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting || (workflowToDelete.hasAgents && deleteConfirmation !== 'DELETE')}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                  isDeleting || (workflowToDelete.hasAgents && deleteConfirmation !== 'DELETE')
                    ? 'bg-red-400'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isCreating && (
        <form onSubmit={handleCreateWorkflow} className="bg-white shadow rounded-lg p-6">
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Workflow Name
            </label>
            <input
              type="text"
              id="name"
              value={newWorkflowName}
              onChange={(e) => setNewWorkflowName(e.target.value)}
              className="mt-1 block w-full rounded-md border-2 border-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 workflow-name bg-gray-50"
              required
            />
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setIsCreating(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              Create
            </button>
          </div>
        </form>
      )}

      {workflows.length === 0 && !isCreating ? (
        <div className="bg-white shadow rounded-lg p-6 text-center">
          <p className="text-gray-500 mb-4">No workflows found. Create your first workflow to get started.</p>
          <button
            onClick={() => setIsCreating(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Create Workflow
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {workflows.map((workflow) => (
            <div
              key={workflow.id}
              className="relative bg-white shadow rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <Link
                to={`/workflows/${workflow.id}`}
                className="block"
              >
                <h3 className="text-lg font-medium text-gray-900">{workflow.name}</h3>
                <p className="mt-2 text-sm text-gray-500">{workflow.description || 'No description'}</p>
                <div className="mt-4 flex items-center text-sm text-gray-500">
                  <span>Blocks: {workflow.blocks.length}</span>
                  <span className="mx-2">•</span>
                  <span>Connections: {workflow.connections.length}</span>
                  <span className="mx-2">•</span>
                  <span>Agents: {workflowAgentCounts[workflow.id] || 0}</span>
                </div>
              </Link>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleDeleteClick(workflow.id, workflow.name);
                }}
                className="absolute top-2 right-2 p-2 text-gray-400 hover:text-red-600"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}; 